import React from "react";
import { tenantLogoUrl } from "../../../../shared/tenantBranding";

export interface ReceivingItem {
  id: number;
  invoice_id: number;
  product_id: number;
  category_id: number | null;
  product_name: string;
  lot_number: string;
  tare_weight: string;
  weight_in_kg: string;
  weight_in_lbs: string;
  stock_unit_of_measurement: number;
  stock_unit_of_measurement_name: string;
  unit_cost: string;
  piece_count: number;
  warehouse_id: number;
  warehouse_name: string;
  warehouse_location_id: number;
  warehouse_location_name: string;
  received_by: number;
  received_by_name: string;
  barcode: string;
  plu: string;
  total_cost: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface SpecSheetTest {
  id: number;
  name: string;
  method: string;
  results: string;
}

export interface WarehouseLocation {
  id: number;
  warehouse_id: number;
  name: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface AllergenPivot {
  in_product: number;
  in_same_plant: number;
}

export interface SpecSheetAllergen {
  id: number;
  name: string;
  pivot: AllergenPivot;
}

export interface Class {
  id: number;
  name: string;
  spec_sheet_tests: SpecSheetTest[];
  spec_sheet_allergens: SpecSheetAllergen[];
}

export interface Product {
  id: number;
  name: string;
  plu: string;
  description_1: string;
  description_2: string | null;
  description_3: string | null;
  disclaimer: string | null;
  label_graphic: string | null;
  label_graphic_id: number | null;
  standard_weight: number | null;
  upper_set_point: number;
  lower_set_point: number;
  stock_unit_of_measure: number;
  selling_unit_of_measure: number;
  shelf_life: number;
  piece_count: number;
  class: Class;
  category: number;
  organic: boolean | null;
  box_type: string | null;
  warehouse_id: number;
  warehouse_location_id: number;
  price_1: number;
  price_2: number;
  price_3: number;
  price_4: number;
  taxable: number;
  tax_1: string | null;
  tax_2: string | null;
  tax_3: string | null;
  unit_cost: number;
  reorder_limit: number;
  reorder_quantity: number;
  storage_condition: string | null;
  packaging_size: string;
  packaging_material: string | null;
  product_image_url: string | null;
  nutrition_facs_url: string | null;
  directions_for_use: string | null;
  country_of_origin: string | null;
  hot_key_1: string;
  hot_key_2: string;
  food_safety: string | null;
  certifications: string | null;
  product_image: string | null;
  product_image_2: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  ingredients_en: string;
  nutrition_facts: string;
  allergens: string[]; // assuming array of strings
  nutrition_facts_url: string;
  warehouse_name: string;
  warehouse_location_name: string;
  average_unit_cost: string;
  receiving_items: ReceivingItem[];
  warehouse_location: WarehouseLocation;
  colour: number | null;
  odour: number | null;
  taste: number | null;
}

interface DropdownOptions {
  value: number;
  label: string;
}

interface SpechSheetProps {
  product: Product;
  productColours: DropdownOptions[];
  productOdours: DropdownOptions[];
  productTastes: DropdownOptions[];
  tenant?: {
    logo_url?: string | null;
    name?: string;
  } | null;
}

const ProductSpecSheet2: React.FC<SpechSheetProps> = ({
  product,
  productColours,
  productOdours,
  productTastes,
  tenant = null,
}) => {
  const logoSrc = tenantLogoUrl(tenant);
  const handlePrint = () => {
    window.print();
  };

  const productDetailsArr = [
    { title: "Product name", value: product.name },
    { title: "Product code", value: product.plu },
    { title: "Product size", value: product.packaging_size },
    { title: "Product material", value: product.packaging_material },
    { title: "Shelf life", value: (product.shelf_life / 365).toFixed(1).replace(/\.0$/, '') + " years" },
    { title: "Storage condition", value: product.storage_condition },
    { title: "Directions for use", value: product.directions_for_use },
    { title: "Certifications", value: product.certifications },
  ];

  return (
    <div className="max-w-5xl mx-auto p-2 bg-white shadow-md rounded-xl space-y-4 print:bg-white print:shadow-none print:p-0">
      <section>
        <table className="w-full table-auto border border-gray-300 text-left text-xs">
          <tbody>
            <tr>
              <td className="border px-4 py-0" rowSpan={2}>
                {logoSrc ? (
                  <img
                    src={logoSrc}
                    alt="Brand logo"
                    width="90px"
                    height="90px"
                  />
                ) : (
                  <span className="text-sm text-gray-500">NO company image</span>
                )}
              </td>
              <td
                className="border px-4 py-0 text-xl text-center font-bold"
                colSpan={4}
              >
                PRODUCT SPECIFICATION SHEET
              </td>
            </tr>
            <tr>
              <td className="border px-4">Version date: March 25, 2025</td>
              <td className="border px-4">Version: 3</td>
              <td className="border px-4">Prepared by: Kathy Rouse</td>
              <td className="border px-4">Approved by: SQF Practitioner</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 space-y-4">
            <table className="w-full table-auto border border-gray-300 text-left text-xs">
              <tbody>
                <tr className="bg-gray-100">
                  <td
                    className="border px-4 py-1 text-center font-bold"
                    colSpan={4}
                  >
                    Product Details
                  </td>
                </tr>
                {productDetailsArr.map(({ title, value }) => (
                  <tr key={title}>
                    <td className="border px-4 py-1" style={{ width: "30%" }}>
                      {title}
                    </td>
                    <td className="border px-4 py-1" colSpan={3}>
                      {value}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="border px-4 py-1" rowSpan={3}>
                    Physical properties
                  </td>
                  <td className="border px-4 py-1" style={{ width: "10%" }}>
                    Colour
                  </td>
                  <td className="border px-4 py-1">
                    {productColours.find((t) => t.value === product.colour)
                      ?.label || null}
                  </td>
                </tr>
                <tr>
                  <td className="border px-4 py-1">Odour</td>
                  <td className="border px-4 py-1">
                    {productOdours.find((t) => t.value === product.odour)
                      ?.label || null}
                  </td>
                </tr>
                <tr>
                  <td className="border px-4 py-1">Taste</td>
                  <td className="border px-4 py-1">
                    {productTastes.find((t) => t.value === product.taste)
                      ?.label || null}
                  </td>
                </tr>
                <tr>
                  <td className="border px-4 py-1">Country of origin</td>
                  <td className="px-4 py-1">{product.country_of_origin}</td>
                </tr>
              </tbody>
            </table>

            {/* Testing */}
            <section>
              <table className="w-full table-auto border border-gray-300 text-left text-xs">
                <tbody>
                  <tr className="bg-gray-100">
                    <td
                      className="border px-4 py-1 text-center font-bold"
                      colSpan={3}
                    >
                      Testing
                    </td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-1 font-bold">Test</td>
                    <td className="border px-4 py-1 font-bold">Method</td>
                    <td className="border px-4 py-1 font-bold">
                      Food Safety parameters
                    </td>
                  </tr>
                  {product.class.spec_sheet_tests.map(
                    ({ id, name, method, results }) => (
                      <tr key={id}>
                        <td className="border px-4 py-1">{name}</td>
                        <td className="border px-4 py-1">{method}</td>
                        <td className="border px-4 py-1">{results}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </section>

            {/* Allergen Checklist */}
            <section>
              <table className="w-full table-auto border border-gray-300 text-xs">
                <tbody>
                  <tr className="bg-gray-100">
                    <td
                      className="border px-4 py-1 text-center font-bold"
                      colSpan={4}
                    >
                      Allergen Checklist
                    </td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-1" style={{ width: "40%" }}>
                      Component
                    </td>
                    <td className="border px-4 py-1">In Product</td>
                    <td className="border px-4 py-1">In Same Plant</td>
                  </tr>
                  {product.class.spec_sheet_allergens.map(
                    ({ id, name, pivot }, index) => (
                      <tr key={id}>
                        <td className="border px-4 py-1">{name}</td>
                        <td className="border px-4 py-1">
                          {pivot.in_product === 1 ? "Yes" : "No"}
                        </td>
                        <td className="border px-4 py-1">
                          {pivot.in_same_plant === 1 ? "Yes" : "No"}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </section>
          </div>
          <div className="space-y-4">
            {product.product_image_url ? (
              <img
                src={product.product_image_url}
                alt="Brand logo"
                width="200px"
                height="200px"
              />
            ) : (
              "No product image"
            )}

            {product.nutrition_facts_url ? (
              <img
                src={product.nutrition_facts_url}
                alt="Nutrition facts"
                width="240px"
                height="240px"
              />
            ) : (
              "No nutritifion facts image"
            )}
          </div>
        </div>
      </section>

      {/* Print/Download Button */}
      <div className="pt-4 border-t border-gray-200 hide-for-print">
        <button
          onClick={handlePrint}
          className="px-6 py-1 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition"
        >
          Print / Download PDF
        </button>
      </div>
    </div>
  );
};

export default ProductSpecSheet2;
